/**
 * @fileoverview Popup Logic Extension
 *
 * A system for managing and coordinating multiple popup components across a website.
 * This module controls popup visibility state, handles popup prioritization,
 * and provides coordination between different popup platforms (Klaviyo, custom, etc.).
 *
 * @module popup-logic
 */

import { type KlaviyoPopupAdapter } from './adapters/klaviyo';
import { type CustomPopupAdapter } from './adapters/custom';
import { type KlaviyoSniffer } from './sniffers/klaviyo';
import { type CustomPopupSniffer } from './sniffers/custom';
import { ConfigOptions, PopupCallback, PopupSniffer } from './types';
import { Popup } from './popup';
import { PopupTimer } from './timer';

declare global {
  interface Window {
    popupMonitor: PopupMonitor;
    Popup: Popup;
    PopupSniffer: PopupSniffer;
    KlaviyoPopupAdapter: KlaviyoPopupAdapter;
    CustomPopupAdapter: CustomPopupAdapter;
    klaviyoSniffer: KlaviyoSniffer;
    customSniffer: CustomPopupSniffer;
    popupTimer: PopupTimer;
  }
}

/**
 * @class PopupMonitor
 * @description Main controller class for managing popup visibility and behavior across the site.
 * Handles popup prioritization, limits the number of popups displayed simultaneously,
 * and coordinates open/close events between different popup systems.
 */
export class PopupMonitor {
  /** @private Map of all registered popups by their ID */
  private popups: Map<string, Popup>;

  /** @private Currently active/visible popup instance or null if none are active */
  private activePopup: Popup | null;

  /** @private Optional callback function to run when any popup opens */
  private globalOnOpen?: PopupCallback;

  /** @private Optional callback function to run when any popup closes */
  private globalOnClose?: PopupCallback;

  /** @private Maximum number of popups allowed to be shown per page view */
  private maxPerPage: number;

  /** @private Counter tracking how many popups have been displayed in the current page session */
  private currentPopupCount: number;

  /**
   * Creates a new PopupMonitor instance
   * @param {ConfigOptions} [options] - Optional configuration settings
   * @param {PopupCallback} [options.onOpen] - Global callback for when any popup opens
   * @param {PopupCallback} [options.onClose] - Global callback for when any popup closes
   * @param {number} [options.maxPerPage=2] - Maximum number of popups to show per page view
   */
  constructor(options?: ConfigOptions) {
    this.popups = new Map();
    this.activePopup = null;
    this.globalOnOpen = options?.onOpen;
    this.globalOnClose = options?.onClose;
    this.maxPerPage = options?.maxPerPage ?? 2;
    this.currentPopupCount = 0;
  }

  /**
   * Sets or updates the global callback functions for popup events
   * @param {Object} callbacks - Object containing callback functions
   * @param {PopupCallback} [callbacks.onOpen] - Function to call when any popup opens
   * @param {PopupCallback} [callbacks.onClose] - Function to call when any popup closes
   */
  setGlobalCallbacks(callbacks: { onOpen?: PopupCallback; onClose?: PopupCallback }) {
    if (callbacks.onOpen !== undefined) {
      this.globalOnOpen = callbacks.onOpen;
    }
    if (callbacks.onClose !== undefined) {
      this.globalOnClose = callbacks.onClose;
    }
  }

  /**
   * Registers a popup with the monitor
   * @param {Popup} popup - Popup instance to register
   */
  addPopup(popup: Popup): void {
    this.popups.set(popup.getId(), popup);

    // Initially hide all popups when added
    // this.hidePopup(popup.getId());
  }

  /**
   * Unregisters and hides a popup
   * @param {string} id - ID of the popup to remove
   */
  removePopup(id: string): void {
    const popup = this.popups.get(id);
    if (popup) {
      this.hidePopup(id);
      this.popups.delete(id);
      if (this.activePopup === popup) {
        this.activePopup = null;
      }
    }
  }

  /**
   * Removes the SSR-generated style tag for a popup
   * @param {string} id - ID of the popup whose style should be cleared
   * @returns {boolean} - True if successful, false if an error occurred
   */
  clearSSRStyle(id: string): boolean {
    try {
      console.log(`Clearing SSR style for popup ${id}`);

      const style = document.getElementById(`popup-logic-style-${id}`);

      if (!style) return true;

      style.remove();

      return true;
    } catch (e) {
      console.error(`Error clearing SSR style`, e);
      return false;
    }
  }

  /**
   * Displays a popup if conditions allow
   * @param {string} id - ID of the popup to show
   * @returns {Promise<boolean>} - Promise resolving to true if popup was shown successfully
   */
  async showPopup(id: string): Promise<boolean> {
    const popup = this.popups.get(id);
    if (!popup) return false;

    // If the incoming popup has forceShow: true, ignore the canShowPopup check
    if (!popup.getAdapter().getForceShow() && !this.canShowPopup()) return false;

    if (this.activePopup && this.activePopup.getId() === id) return true;

    if (!this.activePopup) {
      this.activePopup = popup;
      if (await popup.show()) {
        this.clearSSRStyle(id);

        this.globalOnOpen?.(popup);

        this.currentPopupCount++;

        return true;
      }
    }

    // If we get here, there is a different popup that is currently open
    if (popup.getPriority() > this.activePopup.getPriority()) {
      this.hidePopup(this.activePopup.getId());
      this.globalOnClose?.(this.activePopup);
      this.activePopup = popup;
      if (await popup.show()) {
        this.clearSSRStyle(id);
        this.globalOnOpen?.(popup);
        this.currentPopupCount++;
        return true;
      }
    }

    // Other popup is more important, so we don't show this one
    return false;
  }

  /**
   * Hides a specific popup
   * @param {string} id - ID of the popup to hide
   * @returns {Promise<boolean>} - Promise resolving to true if popup was hidden successfully
   */
  async hidePopup(id: string): Promise<boolean> {
    const popup = this.popups.get(id);
    if (!popup) return true;

    if (!(await popup.hide())) {
      return false;
    }

    this.globalOnClose?.(popup);
    if (this.activePopup === popup) {
      this.activePopup = null;
    }

    return true;
  }

  /**
   * Hides all currently visible popups
   * @returns {boolean} - True if all popups were hidden successfully
   */
  hideAll(): boolean {
    if (
      [...this.popups].every(([id, popup]) => {
        if (popup.isVisible()) {
          return this.hidePopup(popup.getId());
        }
        return true;
      })
    ) {
      this.activePopup = null;

      return true;
    }

    return false;
  }

  /**
   * Retrieves a popup by its ID
   * @param {string} id - ID of the popup to retrieve
   * @returns {Popup|undefined} - The requested popup or undefined if not found
   */
  getPopup(id: string): Popup | undefined {
    return this.popups.get(id);
  }

  /**
   * Gets the currently active popup
   * @returns {Popup|null} - Currently active popup or null if none are active
   */
  getActivePopup(): Popup | null {
    return this.activePopup;
  }

  /**
   * Gets the maximum number of popups allowed per page
   * @returns {number} - Maximum number of popups allowed
   */
  getMaxPerPage(): number {
    return this.maxPerPage;
  }

  /**
   * Gets the current count of popups that have been shown
   * @returns {number} - Number of popups shown in the current page session
   */
  getCurrentPopupCount(): number {
    return this.currentPopupCount;
  }

  /**
   * Determines if another popup can be shown
   * @returns {boolean} - True if another popup can be displayed
   */
  canShowPopup(): boolean {
    return this.currentPopupCount < this.maxPerPage && this.activePopup === null;
  }

  /**
   * Gets the next popup that hasn't been shown yet
   * @returns {Popup|undefined} - Next popup to show or undefined if none are available
   */
  getNextPopup(): Popup | undefined {
    if (!this.canShowPopup()) return;

    // Find the next popup that has shown: false
    for (const popup of this.popups.values()) {
      if (!popup.getShown()) {
        return popup;
      }
    }

    return undefined;
  }

  /**
   * Gets all registered popups
   * @returns {Map<string, Popup>} - Map of all popup instances by ID
   */
  getPopups(): Map<string, Popup> {
    return this.popups;
  }
}

//@ts-ignore
window.PopupMonitor = PopupMonitor;
